import React, { createContext, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, loadSession } from "@/api/client";
import { resolveAuthPortal } from "./resolveAuthPortal";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem("njm_user");
    return s ? JSON.parse(s) : null;
  });
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Route-aware session bootstrap. The Partner Agency portal (/agency/*) has its OWN
    // password-auth + its OWN /v1/agency-auth/me — it must NOT call the staff /auth/me (that
    // fired a spurious 401 on every agency page load). Staff routes keep the resilient /auth/me.
    //
    // SECURITY FIX (2026-09, live prod finding — Bavna's #24): this used to be
    // `window.location.pathname.startsWith("/agency")`, decided fresh on every mount/reload.
    // A user who is genuinely, separately authenticated as BOTH staff (njm_access cookie, e.g.
    // an NJM employee with a staff tab open) AND agency (njm_agency_access cookie) in the SAME
    // browser could type any non-/agency URL (even one that matches nothing, like "/staff")
    // and this would silently call the STAFF /auth/me instead, rendering the full staff
    // AppShell. Confirmed via live repro this does NOT let a pure agency-only session (no
    // staff cookie ever set) reach staff data — /auth/me correctly 401s and Protected
    // redirects to /login — but it's still a real cross-portal identity leak for anyone
    // holding both cookies, which is exactly the class of bug the cookie separation exists to
    // prevent. Fix: track which portal the user last explicitly AUTHENTICATED into (set at
    // each login() below, not derived from the current URL), so navigating away from /agency
    // doesn't silently re-arm the staff check.
    const lastPortal = localStorage.getItem("njm_last_portal");
    // PM Client Portal Preview fix (2026-09) — a THIRD, independent branch, checked first and
    // by pathname alone (never via `lastPortal`): unlike agency, a client/preview tab always
    // lives under this one dedicated path prefix and never legitimately navigates anywhere
    // else, so there's no #24-shaped "which portal did they last log into" ambiguity to guard
    // against here — the worst case of a wrong guess is a 401, not a privilege leak, since
    // `/v1/client-portal/me` can only ever return client-shaped data or reject. This also
    // covers genuine (non-preview) client logins, which previously rode on the generic
    // `/auth/me` too — closing that shared-endpoint gap for both cases at once, not just preview.
    const isClientPortal = window.location.pathname.startsWith("/client-portal");
    // "Preview as Agency" (2026-10) — the popup verify_agency_preview redirects to lands on
    // `/agency?preview=1` in the SAME origin/localStorage as whichever staff tab triggered it,
    // so `lastPortal` there is (correctly, for that staff tab) "staff" — resolveAuthPortal would
    // otherwise call the STAFF /auth/me for this popup and render the staff AppShell instead of
    // the agency one. Deliberately NARROW (only this one exact, backend-set query marker, never
    // derived from `lastPortal` or written back to it) so the #24 lastPortal-priority fix above
    // stays intact for every other /agency navigation. Worst case if spoofed: `/v1/agency-auth/me`
    // 401s (no real session cookie to back it) — same "can't leak, only misroute" reasoning as
    // the isClientPortal branch above.
    const isPreviewAgencyPopup = window.location.pathname.startsWith("/agency") && new URLSearchParams(window.location.search).get("preview") === "1";
    const isAgency = !isClientPortal && (isPreviewAgencyPopup || resolveAuthPortal(window.location.pathname, lastPortal) === "agency");
    const meUrl = isClientPortal ? "/v1/client-portal/me" : isAgency ? "/v1/agency-auth/me" : "/auth/me";
    const apply = (r) => {
      const u = isClientPortal ? { ...r.data, role: "client" } : isAgency ? { ...r.data, role: "partner_agency" } : r.data.user;
      setUser(u);
      setScopes(isClientPortal || isAgency ? [] : (r.data.scopes || []));
      localStorage.setItem("njm_user", JSON.stringify(u));
    };
    const attempt = () => loadSession(meUrl);
    attempt().then((r) => {
      apply(r);
      setLoading(false);
    }).catch(() => {
      setTimeout(() => {
        attempt().then((r) => {
          apply(r);
        }).catch(() => {
          // Staff: only wipe if login() hasn't since written a token (see race notes below).
          // Agency/Client Portal: cookie-based, no njm_token — a genuine 401 means "not signed
          // in" (or, for a preview tab, "the preview expired"), so clear either way.
          if (isAgency || isClientPortal || !localStorage.getItem("njm_token")) {
            localStorage.removeItem("njm_user");
            setUser(null);
          }
        }).finally(() => setLoading(false));
      }, 800);
    });
  }, []);

  const login = async (email, password) => {
    // Wave 1 (2026-10, frontend data cache) security requirement: a login always clears the
    // in-memory query cache first — whether it's a different person signing in on this device,
    // or the same person re-authenticating, neither should ever be able to see a PREVIOUS
    // session's cached data for even one render.
    queryClient.clear();
    const r = await api.post("/auth/login", { email, password });
    if (r.data.must_reset_password) {
      // PM-account password auth (2026-02) — a correct temp-password login does not establish
      // a session; the caller (Login.jsx) must render the forced "set a new password" step and
      // call completePasswordReset() below with this token.
      return { must_reset_password: true, reset_token: r.data.reset_token };
    }
    localStorage.setItem("njm_token", r.data.token);
    localStorage.setItem("njm_user", JSON.stringify(r.data.user));
    localStorage.setItem("njm_last_portal", "staff"); // 2026-09 security fix — see mount effect above
    setUser(r.data.user);
    setScopes(await fetchScopesResilient());
    return r.data.user;
  };

  // Freelancer Job Assignment Tool testing pass (2026-02, testing agent RCA): login()'s own
  // /auth/me call must NEVER be able to trigger the global forced-logout-redirect on a
  // transient blip — same one-retry-resilience + silent401 the mount effect above already
  // uses. Before this fix, this call was non-silent, so the exact same rare transient blip that
  // the mount effect already tolerates could instead wipe the just-established session out
  // from under a genuinely successful password login.
  const fetchScopesResilient = async () => {
    try {
      const me = await api.get("/auth/me", { silent401: true });
      return me.data.scopes || [];
    } catch {
      await new Promise((res) => setTimeout(res, 800));
      try {
        const me = await api.get("/auth/me", { silent401: true });
        return me.data.scopes || [];
      } catch {
        return [];
      }
    }
  };

  const completePasswordReset = async (resetToken, newPassword) => {
    queryClient.clear(); // Wave 1 (2026-10) — same fresh-session boundary as login() above
    const r = await api.post("/auth/set-password", { reset_token: resetToken, new_password: newPassword });
    localStorage.setItem("njm_token", r.data.token);
    localStorage.setItem("njm_user", JSON.stringify(r.data.user));
    localStorage.setItem("njm_last_portal", "staff"); // 2026-09 security fix — see mount effect above
    setUser(r.data.user);
    setScopes(await fetchScopesResilient());
    return r.data.user;
  };

  // Individual staff logins (2026-11) — consumes the one-time invite link an admin sent
  // (modules/admin.py's create-staff / resend-invite) via POST /auth/staff/set-password, then
  // signs the person straight in, same shape as completePasswordReset above.
  const completeStaffInvite = async (token, newPassword) => {
    queryClient.clear();
    const r = await api.post("/auth/staff/set-password", { token, password: newPassword });
    localStorage.setItem("njm_token", r.data.token);
    localStorage.setItem("njm_user", JSON.stringify(r.data.user));
    localStorage.setItem("njm_last_portal", "staff");
    setUser(r.data.user);
    setScopes(await fetchScopesResilient());
    return r.data.user;
  };

  const logout = async () => {
    queryClient.clear(); // Wave 1 (2026-10) — never leave this session's data in memory past logout
    try { await api.post("/auth/logout"); } catch { /* cookies may already be gone */ }
    localStorage.removeItem("njm_token");
    localStorage.removeItem("njm_user");
    localStorage.removeItem("njm_last_portal");
    setUser(null); setScopes([]);
    window.location.href = "/login";
  };

  const hasScope = (s) => scopes.includes(s);

  return (
    <AuthCtx.Provider value={{ user, scopes, hasScope, loading, login, logout, completePasswordReset, completeStaffInvite }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
